import type { Building } from "building-types/building/buildingTypes";
import { FloorType } from "building-types/floors/floorTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { LevelFloorAreaTakeoff } from "building-types/rooms/takeoffTypes";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolygonGeometryNew, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import type { Face } from "geom/brep";
import { getFloorMaterialKeyMeasures } from "lib/house/dataSequences/builtin/floorMaterial";
import { FloorMaterialAreaColorTable } from "lib/house/dataSequences/builtin/floorMaterialSummary";

export function makeSheetGeometryForFloorFill({ building, levelId, renderStyleMode }: PlanGenerationContext) {
  if (renderStyleMode !== "showroomFloorPlan") {
    return [];
  }

  const result: SheetGeometry[] = [];

  for (const room of Object.values(building.rooms)) {
    if (room.level.id !== levelId) {
      continue;
    }

    const roomOutline = building.roomOutlineTable[room.id]?.INSIDE_OF_MASS_IGNORE_VOID;
    if (!roomOutline) {
      continue;
    }

    result.push(
      sheetPolygonGeometryNew({
        handle: room.roomHandle,
        paths: [[roomOutline]],
        category: SheetGeometryCategory.FLOOR_FILL,
      }),
    );
  }

  return result;
}

export function makeSheetGeometryForDecks({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const { floorSystems, levels } = building;

  for (const floorSystemId in floorSystems) {
    const floorSystem = floorSystems[floorSystemId];
    if (floorSystem.levelId !== levelId) {
      continue;
    }
    const deckFloors = floorSystem.roomClassFloors[RoomClass.DECK];
    if (!deckFloors) {
      continue;
    }
    for (const floor of deckFloors) {
      if (floor.floorType !== FloorType.SOLID) {
        continue;
      }

      result.push(
        sheetPolylineGeometryNew({
          handle: levels[levelId].handle,
          paths: floor.floorPlate.perimeterPolyLine2s,
        }),
      );
    }
  }

  return result;
}

function forEachFloorArea(
  building: Building,
  levelId: string,
  takeoffType: FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA | FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA,
  callback: (levelTakeoff: LevelFloorAreaTakeoff) => void,
) {
  const { floorAreaTakeoffs } = building;

  for (const levelAreaIndex in floorAreaTakeoffs[takeoffType]?.levelFloorAreas) {
    const levelTakeoff = floorAreaTakeoffs[takeoffType]?.levelFloorAreas[levelAreaIndex];

    if (levelAreaIndex !== levelId) {
      continue;
    }

    callback(levelTakeoff);
  }
}

export function makeSheetGeometryFromConditionedFloorAreas({ building, levelId }: PlanGenerationContext) {
  const result: SheetGeometry[] = [];
  const { levels } = building;

  const handle = levels[levelId].handle;

  forEachFloorArea(building, levelId, FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA, (levelTakeoff) => {
    result.push(
      sheetPolygonGeometryNew({
        handle,
        paths: levelTakeoff.floorAreaPolygons,
      }),
    );
  });

  return result;
}

export function makeSheetGeometryFromUnconditionedFloorAreas({ building, levelId }: PlanGenerationContext) {
  const result: SheetGeometry[] = [];
  const { levels } = building;

  const handle = levels[levelId].handle;

  forEachFloorArea(building, levelId, FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA, (levelTakeoff) => {
    result.push(
      sheetPolygonGeometryNew({
        handle,
        paths: levelTakeoff.floorAreaPolygons,
      }),
    );
  });

  return result;
}

export function makeFloorMaterialGeometries({ building, levelId }: PlanGenerationContext) {
  const result: SheetGeometry[] = [];

  const { floorSystems, levels } = building;
  const floorMaterialKeyMeasures = getFloorMaterialKeyMeasures(floorSystems);

  let i = 0;
  for (const key in floorMaterialKeyMeasures) {
    const floorMaterial = floorMaterialKeyMeasures[key];
    const floorFillColor = FloorMaterialAreaColorTable[i % FloorMaterialAreaColorTable.length];

    if (floorMaterial.levelsInfo[levelId]) {
      const faces = floorMaterial.levelsInfo[levelId].facesArrayPerSrc.reduce((acc, f) => {
        f.faces.forEach((face) => {
          acc.push(face);
        });
        return acc;
      }, [] as Face[]);
      result.push(
        sheetPolygonGeometryNew({
          handle: levels[levelId].handle,
          paths: faces.map((f) => f.loops),
          fillColor: floorFillColor,
        }),
      );
    }

    ++i;
  }
  return result;
}
